// Cálculo de puntuación por turno. Configurable desde config.json (sección scoring).
import { TILE_TYPE } from '../utils/constants.js';
import { letterCount } from '../utils/unicode.js';

export class ScoreManager {
  constructor(scoringConfig) {
    this.cfg = scoringConfig;
  }

  /**
   * Calcula la puntuación de un turno y devuelve el desglose para mostrar.
   * @param {object} ctx
   * @param {object[]} ctx.playedTiles fichas colocadas desde la mano este turno
   * @param {number} ctx.reusedTiles nº de fichas del tablero reorganizadas
   * @param {string[]} ctx.newWords palabras nuevas válidas creadas este turno
   * @param {Array} ctx.evolutions evoluciones detectadas
   * @param {boolean} ctx.rackEmptied ¿vació el atril?
   * @param {Array} ctx.familiesCompleted familias completadas
   * @param {number} ctx.evolutionChain cadena de evoluciones acumulada
   */
  computeTurnScore(ctx) {
    const c = this.cfg;
    const breakdown = { tiles: 0, words: 0, bonuses: 0, multiplier: 1, total: 0, lines: [] };

    // --- Puntos por fichas jugadas ---
    for (const tile of ctx.playedTiles || []) {
      let pts = 0;
      switch (tile.type) {
        case TILE_TYPE.LETTER: pts = c.letter; break;
        case TILE_TYPE.SYLLABLE: pts = c.syllable; break;
        case TILE_TYPE.PREFIX: pts = c.prefix; break;
        case TILE_TYPE.SUFFIX: pts = c.suffix; break;
        default: pts = 0;
      }
      breakdown.tiles += pts;
    }
    if (breakdown.tiles) breakdown.lines.push({ label: 'Fichas jugadas', points: breakdown.tiles });

    // --- Reutilización de fichas del tablero ---
    if (ctx.reusedTiles > 0) {
      const reuse = ctx.reusedTiles * c.reuseTile;
      breakdown.tiles += reuse;
      breakdown.lines.push({ label: `Reutilizar ${ctx.reusedTiles} ficha(s)`, points: reuse });
    }

    // --- Palabras nuevas ---
    const newWordsCount = (ctx.newWords || []).length;
    if (newWordsCount > 0) {
      const wp = newWordsCount * c.newWord;
      breakdown.words += wp;
      breakdown.lines.push({ label: `${newWordsCount} palabra(s) nueva(s)`, points: wp });
    }

    // --- Palabra larga ---
    const hasLong = (ctx.newWords || []).some((w) => letterCount(w) >= c.longWordThreshold);
    if (hasLong) {
      breakdown.bonuses += c.longWord;
      breakdown.lines.push({ label: `Palabra de ${c.longWordThreshold}+ letras`, points: c.longWord });
    }

    // --- Evoluciones (con cadena creciente) ---
    const evoCount = (ctx.evolutions || []).length;
    if (evoCount > 0) {
      const chain = ctx.evolutionChain || 0;
      const chainMult = c.evolutionChainBase + chain * c.evolutionChainStep;
      const evoPts = Math.round(evoCount * c.evolution * chainMult);
      breakdown.bonuses += evoPts;
      breakdown.lines.push({
        label: `${evoCount} evolución(es)${chainMult > 1 ? ` x${chainMult.toFixed(2)}` : ''}`,
        points: evoPts,
      });
    }

    // --- Familias completadas ---
    const famCount = (ctx.familiesCompleted || []).length;
    if (famCount > 0) {
      const fp = famCount * c.familyComplete;
      breakdown.bonuses += fp;
      breakdown.lines.push({ label: `${famCount} familia(s) completada(s)`, points: fp });
    }

    // --- Vaciar el atril ---
    if (ctx.rackEmptied) {
      breakdown.bonuses += c.emptyRack;
      breakdown.lines.push({ label: '¡Atril vacío!', points: c.emptyRack });
    }

    // --- Multiplicador por nº de palabras en el turno ---
    if (newWordsCount >= 3) breakdown.multiplier = c.threeWordsMultiplier;
    else if (newWordsCount === 2) breakdown.multiplier = c.twoWordsMultiplier;

    const subtotal = breakdown.tiles + breakdown.words + breakdown.bonuses;
    breakdown.total = Math.round(subtotal * breakdown.multiplier);
    if (breakdown.multiplier > 1) {
      breakdown.lines.push({ label: `Multiplicador x${breakdown.multiplier}`, points: breakdown.total - subtotal });
    }

    return breakdown;
  }
}
